import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
  Form, FormControl, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  HelpCircle, Clock, CheckCircle2, AlertCircle, CircleDot, User,
  ChevronDown, ChevronRight, MessageSquare,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useGaContext } from "@/hooks/useGaContext";

// ── Helpers ───────────────────────────────────────────────────────────────────

const REQUEST_TYPES: Record<string, string> = {
  setup: "Platform Setup",
  onboarding: "Client Onboarding",
  service: "Service / Account Help",
  training: "Training & Guidance",
  technical: "Technical Issue",
  other: "Other",
};

const PRIORITIES: Record<string, { label: string; className: string }> = {
  low: { label: "Low", className: "bg-muted text-muted-foreground border-border" },
  normal: { label: "Normal", className: "bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800" },
  high: { label: "High", className: "bg-orange-50 dark:bg-orange-950 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-800" },
  urgent: { label: "Urgent", className: "bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800" },
};

const STATUSES: Record<string, { label: string; icon: any; className: string }> = {
  open: { label: "Open", icon: CircleDot, className: "bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800" },
  in_progress: { label: "In Progress", icon: Clock, className: "bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800" },
  resolved: { label: "Resolved", icon: CheckCircle2, className: "bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800" },
  closed: { label: "Closed", icon: AlertCircle, className: "bg-muted text-muted-foreground border-border" },
};

const updateSchema = z.object({
  status: z.string().min(1),
  gaNote: z.string().max(2000).optional(),
});

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUSES[status] || STATUSES.open;
  const Icon = cfg.icon;
  return (
    <Badge variant="outline" className={cn("text-xs gap-1", cfg.className)}>
      <Icon className="w-3 h-3" />{cfg.label}
    </Badge>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const cfg = PRIORITIES[priority] || PRIORITIES.normal;
  return (
    <Badge variant="outline" className={cn("text-xs", cfg.className)}>{cfg.label}</Badge>
  );
}

// ── Update Dialog ─────────────────────────────────────────────────────────────

function UpdateDialog({ gaId, request, onClose }: { gaId: string; request: any; onClose: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof updateSchema>>({
    resolver: zodResolver(updateSchema),
    defaultValues: { status: request.status, gaNote: request.gaNote ?? "" },
  });

  const mutation = useMutation({
    mutationFn: (v: z.infer<typeof updateSchema>) =>
      apiRequest("PATCH", `/api/ga/${gaId}/service-requests/${request.id}`, v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/ga/${gaId}/service-requests`] });
      toast({ title: "Request updated" });
      onClose();
    },
    onError: (e: any) => toast({ title: "Update failed", description: e.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Update Request</DialogTitle>
          <DialogDescription className="line-clamp-2">{request.title}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(v => mutation.mutate(v))} className="space-y-4">
            <FormField control={form.control} name="status" render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="gaNote" render={({ field }) => (
              <FormItem>
                <FormLabel>Note to broker <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Add context, next steps, or a resolution summary visible to the broker…"
                    className="min-h-[100px] resize-none"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={mutation.isPending} className="bg-[#1737A2] hover:bg-[#1737A2]/90">
                {mutation.isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ── Request Row ───────────────────────────────────────────────────────────────

function RequestRow({ gaId, req }: { gaId: string; req: any }) {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);

  const typeLabel = REQUEST_TYPES[req.requestType] ?? req.requestType;
  const date = new Date(req.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  return (
    <>
      {updating && <UpdateDialog gaId={gaId} request={req} onClose={() => setUpdating(false)} />}
      <div className="border rounded-lg overflow-hidden">
        <button
          className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted/50 transition-colors"
          onClick={() => setExpanded(e => !e)}
        >
          <span className="text-muted-foreground">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground truncate">{req.title}</p>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              <User className="inline w-3 h-3 mr-1" />
              {req.brokerName || req.brokerAccountId} · {typeLabel} · {date}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <PriorityBadge priority={req.priority} />
            <StatusBadge status={req.status} />
          </div>
        </button>

        {expanded && (
          <div className="border-t bg-muted px-4 py-3 space-y-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Request details</p>
              <p className="text-sm text-foreground whitespace-pre-wrap">{req.description}</p>
            </div>
            {req.gaNote && (
              <>
                <Separator />
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Your note to broker</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{req.gaNote}</p>
                </div>
              </>
            )}
            <div className="flex justify-end pt-1">
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={e => { e.stopPropagation(); setUpdating(true); }}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                Update / Respond
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function GAServiceRequests() {
  const { gaId } = useGaContext();
  const [statusFilter, setStatusFilter] = useState("open");

  const { data: requests = [], isLoading } = useQuery<any[]>({
    queryKey: [`/api/ga/${gaId}/service-requests`],
    enabled: !!gaId,
  });

  const filtered = statusFilter === "all"
    ? requests
    : requests.filter(r => r.status === statusFilter);

  const counts: Record<string, number> = { all: requests.length };
  requests.forEach(r => { counts[r.status] = (counts[r.status] ?? 0) + 1; });

  const TABS = [
    { key: "all", label: "All" },
    { key: "open", label: "Open" },
    { key: "in_progress", label: "In Progress" },
    { key: "resolved", label: "Resolved" },
    { key: "closed", label: "Closed" },
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Broker Service Requests</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Incoming setup and service requests from your broker network.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Open", key: "open", color: "text-blue-600 dark:text-blue-400" },
          { label: "In Progress", key: "in_progress", color: "text-amber-600 dark:text-amber-400" },
          { label: "Resolved", key: "resolved", color: "text-emerald-600 dark:text-emerald-400" },
          { label: "Total", key: "all", color: "text-foreground" },
        ].map(({ label, key, color }) => (
          <Card key={key} className="cursor-pointer hover:shadow-sm transition-shadow" onClick={() => setStatusFilter(key)}>
            <CardContent className="pt-4 pb-4">
              <p className={cn("text-2xl font-bold", color)}>{counts[key] ?? 0}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base">Requests</CardTitle>
            <div className="flex gap-1 flex-wrap">
              {TABS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setStatusFilter(key)}
                  className={cn(
                    "px-2.5 py-1 text-xs rounded-md transition-colors",
                    statusFilter === key
                      ? "bg-[#1737A2] text-white"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {label}
                  {(counts[key] ?? 0) > 0 && (
                    <span className={cn("ml-1", statusFilter === key ? "text-blue-200" : "text-muted-foreground")}>
                      {counts[key]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => <div key={i} className="animate-pulse h-16 bg-muted rounded-lg" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-border rounded-xl">
              <HelpCircle className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                {statusFilter === "all" ? "No service requests yet." : `No ${statusFilter.replace("_", " ")} requests.`}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(req => <RequestRow key={req.id} gaId={gaId!} req={req} />)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
